import React, { useEffect, useMemo, useRef } from 'react';
import WidgetPageShell from '@components/common/WidgetPageShell';
import WidgetNav from '@components/common/WidgetNav';
import FooterBrand from '@components/common/FooterBrand';
import mockInvestmentIndicators from '@data/mockInvestmentIndicators';
import { mapInvestmentIndicatorsApiToPageData } from '@apis/investmentIndicatorsMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import { useInvestmentBasicInfo } from '@hooks/useInvestmentBasicInfo';
import { useInvestmentConsensus } from '@hooks/useInvestmentConsensus';
import { useInvestmentFinancials } from '@hooks/useInvestmentFinancials';
import { useInvestmentSecurityAttributes } from '@hooks/useInvestmentSecurityAttributes';
import { useInvestmentTechnical } from '@hooks/useInvestmentTechnical';
import { useInvestmentTechnicalSignals } from '@hooks/useInvestmentTechnicalSignals';
import { notify } from '@utils/notify';
import InvestmentQuickLinksRow from './components/InvestmentQuickLinksRow';
import InvestmentIndicatorCardsSection from './components/InvestmentIndicatorCardsSection';
import InvestmentInformationSection from './components/InvestmentInformationSection';
import './InvestmentIndicatorsPage.less';

/**
 * Investment Indicators screen (route /investment-indicators).
 * Web: 3164:13446 (626 × 2662). Mobile: 3164:14749 (379 × 4621).
 * Render order: nav → quick links → key indicator cards → info tables → footer.
 */
const InvestmentIndicatorsPage: React.FC = () => {
  const basicInfoQuery = useInvestmentBasicInfo();
  const consensusQuery = useInvestmentConsensus();
  const securityAttributesQuery = useInvestmentSecurityAttributes();
  const technicalQuery = useInvestmentTechnical();
  const financialsQuery = useInvestmentFinancials();
  const technicalSignalsQuery = useInvestmentTechnicalSignals();
  const hasNotifiedQueryError = useRef(false);

  const data = useMemo(
    () => mapInvestmentIndicatorsApiToPageData(
      {
        basicInfo: basicInfoQuery.data,
        consensus: consensusQuery.data,
        securityAttributes: securityAttributesQuery.data,
        technical: technicalQuery.data,
        financials: financialsQuery.data,
        technicalSignals: technicalSignalsQuery.data,
      },
      mockInvestmentIndicators,
    ),
    [
      basicInfoQuery.data,
      consensusQuery.data,
      securityAttributesQuery.data,
      technicalQuery.data,
      financialsQuery.data,
      technicalSignalsQuery.data,
    ],
  );

  const firstQueryError = [
    basicInfoQuery.error,
    consensusQuery.error,
    securityAttributesQuery.error,
    technicalQuery.error,
    financialsQuery.error,
    technicalSignalsQuery.error,
  ].find((error) => error && shouldNotifyDataRequestError(error));

  useEffect(() => {
    if (!firstQueryError) {
      hasNotifiedQueryError.current = false;
      return;
    }
    if (hasNotifiedQueryError.current) return;
    hasNotifiedQueryError.current = true;
    notify({
      level: 'error',
      message:
        getApiParamErrorNotificationMessage(firstQueryError) ??
        '投資指標データの取得に失敗しました。',
    });
  }, [firstQueryError]);

  return (
    <WidgetPageShell className="investment-indicators-page">
      <WidgetNav />
      <main className="investment-indicators-page__body">
        <InvestmentQuickLinksRow links={data.quickLinks} nodeId="3164:13454" />
        <InvestmentIndicatorCardsSection indicators={data.indicators} />
        <InvestmentInformationSection columns={data.informationColumns} />
        <FooterBrand className="investment-indicators-page__footer" />
      </main>
    </WidgetPageShell>
  );
};

export default InvestmentIndicatorsPage;
